import { ClockIcon } from "@heroicons/react/24/outline";
import { ReactNode, useMemo, useState } from "react";
import { UserActivityStats } from "../../../models/DashboardStats";
import { useNextCronCountdown } from "../../../data/useNextCronCountdown";
import classNames from "../../../utils/classNames";
import DashboardTile from "../../dashboard/DashboardTile";
import LoadingWheel from "../LoadingWheel";
import { getUserActivityTotal } from "./activityTotals";
import { ACTIVITY_METRICS, ActivityMetricKey } from "./activityMetrics";

// Ordered row keys: alphabetical users, with a "Total" row pinned to the top when present.
function buildTableRowKeys(
  activityByUser?: { [key: string]: UserActivityStats },
  activityTotal?: UserActivityStats
): string[] {
  const sortedUserKeys = Object.keys(activityByUser ?? {})
    .filter((key) => key !== "Total")
    .sort((a, b) => a.localeCompare(b));

  return activityTotal != null ? ["Total", ...sortedUserKeys] : sortedUserKeys;
}

export interface IActivityTableProps {
  title: string;
  rowLabelColumn: string;
  activityByUser?: { [key: string]: UserActivityStats };
  activityTotal?: UserActivityStats;
  daysInPeriod: number;
  headerControls?: ReactNode;
  isLoading?: boolean;
  selectedMetric?: ActivityMetricKey;
  onMetricSelect?: (key: ActivityMetricKey) => void;
}

export default function ActivityTable({
  title,
  rowLabelColumn,
  activityByUser,
  activityTotal,
  daysInPeriod,
  headerControls,
  isLoading = false,
  selectedMetric,
  onMetricSelect,
}: IActivityTableProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const countdown = useNextCronCountdown();

  const tableRowKeys = useMemo(
    () => buildTableRowKeys(activityByUser, activityTotal),
    [activityByUser, activityTotal]
  );
  // Daily/hourly averages only make sense across a multi-day range.
  const showDailyAvg = daysInPeriod > 1;

  // Grey out empty/zero cells so real activity stands out in the brand colour.
  const cellClass = (val: number | string | undefined | null, stripe = false) =>
    `${val === 0 || val === "—" || val === undefined || val === null ? "text-gray-400" : "text-primary-600"} font-bold text-md ${stripe ? "bg-primary-50" : ""}`;

  const cellTextClass = (val: number | string | undefined | null) =>
    val === 0 || val === "—" || val === undefined || val === null
      ? "text-gray-400"
      : "text-primary-600";

  const selectable = !!onMetricSelect;

  return (
    <DashboardTile span={2}>
      <div className="p-3">
        <div className="mb-3 flex w-full items-start justify-between gap-4">
          <div className="prose min-w-0">
            <h3 className="mb-0">
              <div
                className="relative inline-flex items-center"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <ClockIcon className="inline-block w-6 h-6 mr-2 text-gray-500 hover:text-primary transition-all duration-200 hover:scale-110 cursor-pointer" />
                {title}
                {/* Hovering the clock shows when the backend cron next refreshes these figures. */}
                {showTooltip && (
                  <div className="absolute left-0 top-full mt-2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs text-white shadow-lg z-10">
                    Recalculating in {countdown}
                  </div>
                )}
              </div>
            </h3>
          </div>
          {headerControls ? <div className="shrink-0">{headerControls}</div> : null}
        </div>

        {isLoading ? (
          <div className="mt-6 min-h-[200px]">
            <LoadingWheel />
          </div>
        ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full border-collapse text-center text-xs">
            <thead>
              <tr>
                <th className="text-left pb-2">{rowLabelColumn}</th>
                <th>Total</th>
                {ACTIVITY_METRICS.map((metric, index) => {
                  const stripe = index % 2 === 0;
                  const isSelected = metric.key === selectedMetric;
                  return (
                    <th
                      key={metric.key}
                      onClick={selectable ? () => onMetricSelect!(metric.key) : undefined}
                      title={selectable ? `Show ${metric.label} in the chart` : undefined}
                      className={classNames(
                        isSelected
                          ? "bg-primary-100 text-primary-700"
                          : stripe
                          ? "bg-primary-50"
                          : "",
                        selectable ? "cursor-pointer select-none hover:bg-primary-100" : ""
                      )}
                    >
                      {metric.label}
                    </th>
                  );
                })}
                {showDailyAvg ? <th className="bg-primary-50">Daily avg</th> : null}
                {showDailyAvg ? <th>Avg/hr</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y">
              {tableRowKeys.length === 0 ? (
                <tr>
                  {/* Span every column: label + Total + each metric (+ the two avg columns when shown). */}
                  <td colSpan={showDailyAvg ? 18 : 16} className="py-6 text-center text-gray-500">
                    No activity data for this period.
                  </td>
                </tr>
              ) : (
                tableRowKeys.map((rowKey) => {
                  const isTotalRow = rowKey === "Total";
                  const row = isTotalRow ? activityTotal : activityByUser?.[rowKey];

                  // Parts-only total (excludes booked/collected/jobs) — drives the daily-average columns.
                  const partsTotal =
                    (row?.createdCount ?? 0) +
                    (row?.removedCount ?? 0) +
                    (row?.imagedCount ?? 0) +
                    (row?.locatedCount ?? 0) +
                    (row?.pricedCount ?? 0) +
                    (row?.listedCount ?? 0) +
                    (row?.pickedCount ?? 0) +
                    (row?.shippedCount ?? 0) +
                    (row?.scrappedCount ?? 0) +
                    (row?.deletedCount ?? 0) +
                    (row?.damagedCount ?? 0);

                  const rowTotal = getUserActivityTotal(row ?? undefined);
                  // Hide users with no activity, but always keep the Total row.
                  if (!isTotalRow && rowTotal === 0) return null;

                  const dailyAvg = showDailyAvg && daysInPeriod >= 1 ? partsTotal / daysInPeriod : null;
                  // Assumes an 8-hour working day.
                  const avgPerHour = dailyAvg != null ? dailyAvg / 8 : null;

                  return (
                    <tr
                      key={rowKey}
                      className={`font-semibold text-center ${isTotalRow ? "border-t-2 border-gray-300 bg-gray-50" : ""}`}
                    >
                      <td className="text-left pt-2" title={rowKey}>
                        {rowKey}
                      </td>
                      <td className={cellClass(rowTotal)}>{rowTotal}</td>
                      {ACTIVITY_METRICS.map((metric, index) => {
                        const stripe = index % 2 === 0;
                        const isSelected = metric.key === selectedMetric;
                        const value = row?.[metric.key];
                        return (
                          <td
                            key={metric.key}
                            className={classNames(
                              "font-bold text-md",
                              cellTextClass(value),
                              isSelected
                                ? "bg-primary-100"
                                : stripe
                                ? "bg-primary-50"
                                : ""
                            )}
                          >
                            {value ?? "—"}
                          </td>
                        );
                      })}
                      {showDailyAvg ? (
                        <td className={cellClass(dailyAvg != null ? dailyAvg : "—", true)}>
                          {dailyAvg != null ? dailyAvg.toFixed(1) : "—"}
                        </td>
                      ) : null}
                      {showDailyAvg ? (
                        <td className={cellClass(avgPerHour != null ? avgPerHour : "—")}>
                          {avgPerHour != null ? avgPerHour.toFixed(1) : "—"}
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </DashboardTile>
  );
}
